import { getClauseUsage, IClauseList } from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { formatDate } from "@/utils/formatDate";
import LaunchIcon from "@mui/icons-material/Launch";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

interface IProps {
    /**
     * The clause to display recent usage for
     */
    clause: IClauseList;
    /**
     * The function to call when the dialog is closed
     */
    onClose: () => void;
}

/**
 * Displays the most recent usage of a clause
 */
export const RecentUsage = ({ clause, onClose }: IProps) => {
    const { data, isPending } = useQuery({
        queryKey: ["frontend-clause-usage", clause.id],
        queryFn: () => getClauseUsage(clause.id),
    });

    const navigate = useNavigate();

    const handleClick = () => {
        if (!data) return;

        const navLink = `/organizations/scopes/${data.id}?clause=${clause.id}`;

        onClose();
        navigate(navLink);
    };

    return (
        <Stack gap={1}>
            <Typography fontWeight={500} gutterBottom>
                Recent Usage
            </Typography>

            {/* Loading Indicator */}
            {isPending && <LoadingIndicator size={18} />}

            {!isPending && (
                <Box
                    sx={{
                        bgcolor: "#F9FAFB",
                        px: 2.5,
                        py: 1.5,
                        borderRadius: 1,
                    }}
                >
                    {/* Recent Usage */}
                    {data && (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems={"center"}
                        >
                            <Stack>
                                <Typography variant="body2" fontWeight={500}>
                                    {data.document_title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Used {formatDate(data.created_at, "PP")}
                                </Typography>
                            </Stack>

                            <div>
                                <IconButton onClick={handleClick}>
                                    <LaunchIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </Stack>
                    )}

                    {/* No Usage History */}
                    {!data && (
                        <Typography variant="body2">
                            No usage history found
                        </Typography>
                    )}
                </Box>
            )}
        </Stack>
    );
};
